import React from "react";
import {useTheme} from "styled-components";
import {useMessages} from "i18n";
import {Switch} from "home/components/OptionsSheet.styles";
import {categoryGroups, categoryOf, templateAssetRange} from "sponsor/sponsorBooking";
import {PAGE_THEME_TEMPLATES, isPageThemeTemplate} from "sponsor/sponsorOptions";
import {SponsorOptionsPanel} from "sponsor/options/SponsorOptionsPanel";
import {optionErrorText} from "sponsor/options/optionErrorText";
import {WallpaperStage} from "sponsor/options/WallpaperPreview";
import {OwnerThemePoster} from "sponsor/takeover/OwnerThemePoster";
import {previewBackdropArt} from "sponsor/takeover/backdropArt";
import {SponsorTakeoverArt} from "sponsor/takeover/SponsorTakeover";
import {TemplatePreviewLauncher} from "sponsor/TemplatePreviewLauncher";
import {TemplateOption} from "sponsor/TemplatePreviewLauncher.styles";
import {
	AssetHint,
	HiddenRadio,
	PickerGrid,
	PreviewBadge,
	PreviewStage,
	placeholderArtUrls,
	TemplateCard,
	TemplateDescription,
	TemplateName,
} from "sponsor/BackdropTemplatePicker.styles";
import {Actions, CardText, GhostButton, SaveButton} from "../components/AccountCards";
import {useAccountPageTheme} from "../hooks/useAccountPageTheme";
import {MAX_THEME_IMAGES, THEME_IMAGE_ACCEPT_ATTRIBUTE, themeImagesPublished} from "./themeValidation";
import {FieldLabel, Hint, HiddenFileInput} from "./BrandSettings.styles";
import {Section, SectionTitle} from "./BrandSheet.styles";
import {TileRemoveButton} from "./TileRemoveButton";
import {TileDeleteButton} from "./TileDeleteButton";
import {
	AddImageLabel,
	CategoryGroup,
	CategoryLede,
	CategoryName,
	Counter,
	ImageRow,
	ImageTile,
	LiveLabel,
	LiveState,
	LiveValue,
	TileButton,
	TileControls,
	TileImage,
	TileState,
	ToggleCopy,
	ToggleName,
	ToggleRow,
	WallpaperOptions,
} from "./PageThemeSection.styles";
import {PickedImageOutcome} from "components/PickedImageOutcome";

/**
 * The download-page theme editor (api#511), rendered as a second section of the SAME branding surface
 * as {@link BrandSettings} — one home for everything that changes what a recipient sees, so there is no
 * second place for it to drift to.
 *
 * Presentational only (epic #26): the entitlement gate, the arity and file rules and the four
 * `/account/brand/theme` calls live in {@link useAccountPageTheme} and
 * {@link ./themeValidation}. Nothing here decides anything.
 *
 * Two things are contract, not styling:
 * 1. The previews mount {@link SponsorTakeoverArt} — the very component the download page mounts — so
 *    what the sender picks here is literally what a recipient gets. A preview-only animation would be a
 *    promise the product does not keep.
 * 2. A non-entitled sender sees the upsell, never a disabled-looking editor, exactly as the brand
 *    editor above does. The api rejects their writes anyway, so exposing controls would only make 400s.
 *
 * Since kitecorp/transferify-ui#1869 the catalogue is offered as the SAME two labelled categories the
 * booking page offers, from the same `categoryGroups` derivation, and a wallpaper opens the SAME option
 * controls — literally `sponsor/options/SponsorOptionsPanel`, not a copy of it. The advertiser-only
 * parts are switched off by props rather than reimplemented: there is no CTA and no ad label on this
 * surface because the panel has never carried either, and no logo because the api's page-theme contract
 * stores no brand mark, so the control is passed `null` and is absent rather than disabled.
 */
export function PageThemeSection() {
	const m = useMessages();
	const copy = m.account.theme;
	const {tokens} = useTheme();
	const theme = useAccountPageTheme();
	const placeholders = placeholderArtUrls(tokens);
	// The previews animate the images the sender has SELECTED, which is what publishing would send. The
	// library's unselected images are theirs to keep but are not part of the theme being composed.
	const chosen = theme.images.filter(image => image.selected).map(image => image.url);
	// Against the whole LIBRARY, not the selection: the api counts every image the account holds, because
	// an unselected one is still stored and still billed for (api#471).
	const atCap = theme.images.length >= MAX_THEME_IMAGES;
	// What publishing the CURRENTLY SELECTED treatment would leave out. Said before Publish rather than
	// discovered after it — though since api#471 those images stay in the library either way.
	const dropped = chosen.length - themeImagesPublished(theme.template, chosen).length;
	/*
	 * The LIVE theme's treatment in words, or `null` when there is nothing live to name — including the
	 * case where the api names a treatment this catalogue has no card for. `PageTheme.template` is bound
	 * to the served enum, which lists all ten; reading a name straight out of the copy object would be
	 * `undefined.name` on the one it does not offer.
	 */
	const savedName = theme.saved.template !== null && isPageThemeTemplate(theme.saved.template)
		? copy.templates[theme.saved.template].name
		: null;
	/*
	 * The line the sender is typesetting, or `null` for a poster with nothing in it. Trimmed exactly as
	 * `ownerPosterLine` trims the served one, so the preview and the recipient's page agree about when a
	 * block appears at all rather than showing an empty scrim over the picture.
	 */
	const typeset = theme.options.subline?.trim() ?? "";
	const posterLine = typeset === "" ? null : typeset;
	const errorTextFor = optionErrorText(theme.optionErrors, copy.optionErrors);

	return (
		<Section>
			<SectionTitle>{copy.title}</SectionTitle>
			<CardText>{copy.description}</CardText>

			{theme.entitled && (
				<>
					<LiveState>
						<LiveLabel>{copy.liveTitle}</LiveLabel>
						<LiveValue>
							{savedName === null
								? copy.liveNone
								: (theme.saved.enabled ? copy.liveOn(savedName) : copy.liveOff(savedName))}
						</LiveValue>
					</LiveState>

					<FieldLabel id="page-theme-template-label">{copy.templateLabel}</FieldLabel>
					{categoryGroups(PAGE_THEME_TEMPLATES).map(({category, templates}) => (
						<CategoryGroup key={category}>
							<CategoryName id={`page-theme-category-${category}`}>
								{copy.categories[category].name}
							</CategoryName>
							<CategoryLede>{copy.categories[category].lede}</CategoryLede>
							{/*
							  * ONE radio name across both grids, exactly as the booking page does: same-name
							  * radios are a single exclusive group to the browser however they are nested, so
							  * a sender arrows straight from the last backdrop into the first wallpaper and
							  * can only ever hold one of the nine. Two names would let them select one in
							  * each category at once.
							  *
							  * The group's accessible name is the field label AND this category's, so a
							  * screen-reader user is told which of the two grids they have entered rather
							  * than hearing "Treatment" twice.
							  */}
							<PickerGrid
								role="radiogroup"
								aria-labelledby={`page-theme-template-label page-theme-category-${category}`}
							>
								{templates.map(template => {
									const range = templateAssetRange(template);
									// How many of the sender's images this treatment would animate, and therefore
									// publish. Shown on the card whenever it is fewer than they have, so the one
									// treatment that takes a single image says so BEFORE they press Publish.
									const used = themeImagesPublished(template, chosen).length;
									// The draft's own settings, so the tile shows the sweep the sender just picked
									// rather than the default one.
									const art = previewBackdropArt(template, chosen, placeholders, theme.options);
									return (
										// The card is a label, so a click anywhere in it selects; the view-larger
										// control is its SIBLING rather than its descendant so a click on it means
										// one thing only (ui#588).
										<TemplateOption key={template}>
											<TemplateCard $selected={template === theme.template}>
												<HiddenRadio
													type="radio"
													// A group name of its own: the sponsor picker uses
													// "sponsor-template", and two radio groups sharing a name would
													// deselect each other if both were ever mounted at once.
													name="page-theme-template"
													/*
													 * NAME and DESCRIPTION, split deliberately. Left to the wrapping
													 * label, the accessible NAME is the whole card — a screen-reader
													 * user hears "Live preview Hero One image, slowly drawn in as the
													 * page settles…" for every option, and the five words that
													 * distinguish them are buried. `aria-label` cuts it to the
													 * treatment, which is also the visible label (WCAG 2.5.3) — but it
													 * REPLACES the label, so the description and both hints would stop
													 * being announced at all. `aria-describedby` puts them back where
													 * they belong: after the name, as description. That matters most
													 * for the hint that says how many images this treatment uses, which
													 * is the whole point of ui#583 and must not be sighted-only.
													 */
													aria-label={copy.templates[template].name}
													aria-describedby={`page-theme-${template}-description page-theme-${template}-arity`
														+ (used < chosen.length ? ` page-theme-${template}-uses` : "")}
													value={template}
													checked={template === theme.template}
													disabled={theme.busy}
													onChange={() => theme.setTemplate(template)}
												/>
												<PreviewStage>
													<SponsorTakeoverArt
														art={art}
														testId={`page-theme-preview-${template}`}
														drive="sweep"
														stage="tile"
													/>
													<PreviewBadge>{copy.templates.previewLabel}</PreviewBadge>
												</PreviewStage>
												<TemplateName>{copy.templates[template].name}</TemplateName>
												<TemplateDescription id={`page-theme-${template}-description`}>
													{copy.templates[template].description}
												</TemplateDescription>
												<AssetHint id={`page-theme-${template}-arity`}>
													{copy.templates.imageHint(range.min, range.max)}
												</AssetHint>
												{used < chosen.length
													&& <AssetHint id={`page-theme-${template}-uses`}>{copy.usesFirst(used)}</AssetHint>}
											</TemplateCard>
										<TemplatePreviewLauncher
											art={art}
											templateName={copy.templates[template].name}
											templateDescription={copy.templates[template].description}
										/>
										</TemplateOption>
									);
								})}
							</PickerGrid>
						</CategoryGroup>
					))}

					{/*
					  * The full-page settings, opened by the treatment that has them (ui#1869).
					  *
					  * WHICH controls appear is not decided here: `SponsorOptionsPanel` reads
					  * `TEMPLATE_OPTIONS`, the same table `validateThemeOptions` judges against and the api's
					  * own validator mirrors. So the wipe's direction appears for the wipe and for nothing
					  * else, and a setting the server would refuse cannot be offered.
					  *
					  * The stage above them is the live one: it mounts the real renderer and the real poster
					  * block, because five of the six settings change nothing a picker tile can show.
					  */}
					{categoryOf(theme.template) === "WALLPAPER" && (
						<WallpaperOptions>
							<WallpaperStage
								art={previewBackdropArt(theme.template, chosen, placeholders, theme.options)}
								badge={copy.templates.previewLabel}
							>
								{stage => posterLine === null
									? null
									: <OwnerThemePoster
										line={posterLine}
										options={theme.options}
										imageUrls={chosen.length > 0 ? chosen : placeholders}
										artLayer={stage}
									/>}
							</WallpaperStage>
							<SponsorOptionsPanel
								template={theme.template}
								options={theme.options}
								setOption={theme.setOption}
								copy={copy.options}
								/* Its own prefix: the sponsor form uses "sponsor-option", and two panels
								   sharing a radio name would steer each other if both were ever mounted. */
								namePrefix="page-theme-option"
								/* No mark: the page-theme contract has no logo asset to store one in. */
								logo={null}
								errorText={errorTextFor}
							/>
						</WallpaperOptions>
					)}

					<FieldLabel>{copy.imagesLabel}</FieldLabel>
					<Hint>{copy.imagesHint}</Hint>
					{theme.images.length === 0 && <Hint>{copy.empty}</Hint>}
					<ImageRow>
						{theme.images.map((image, index) => {
							const position = index + 1;
							return (
								<ImageTile key={image.assetId}>
									{/* Decorative: the sender is looking at their own upload, and every control
									    beside it is individually labelled by position. */}
									<TileImage src={image.url} alt="" $inTheme={image.selected}/>
									{/*
									 * ui#760: an image that is IN the theme carries its removal on the picture's own
									 * top-right corner, so the gesture sits on the thing it acts on.
									 *
									 * ui#686's semantics exactly, unchanged: this takes the picture OUT of the
									 * selection and KEEPS it in the library — the bin (`TileDeleteButton`) below is
									 * the only control that destroys one (api#471). The order arrows stay gone, because the
									 * treatments draw from the set rather than march through it, so the position an
									 * arrow moved was never what a recipient saw.
									 *
									 * Disabled while a write is in flight, like every other control here: a save
									 * submits the selection as it stood when Publish was pressed and adopts the
									 * server's answer back, so an edit made mid-request would be silently undone the
									 * moment the response landed.
									 */}
									{image.selected && (
										<TileRemoveButton
											label={copy.removeAria(position)}
											title={copy.remove}
											disabled={theme.busy}
											onClick={() => theme.unuseImage(image.assetId)}
										/>
									)}
									<TileState $inTheme={image.selected}>
										{image.selected ? copy.inTheme : copy.notInTheme}
									</TileState>
									{/* Only an UNSELECTED tile still has a control row — putting the picture back, and
									    deleting it for good. A selected tile's one affordance is the overlay above,
									    so there is no empty row left under it. */}
									{!image.selected && (
										<TileControls>
											{/* Never bounded by the treatment's maximum: ui#583 made a write
											    send the slice it animates, and `publishDrops` says what that
											    leaves out. Refusing the click instead would be the greyed-out
											    control with no explanation that ui#583 removed. */}
											<TileButton
												aria-label={copy.useAria(position)}
												title={copy.use}
												disabled={theme.busy}
												onClick={() => theme.useImage(image.assetId)}
											>
												+
											</TileButton>
											{/* The api refuses this while the image is live, so the control
											    says WHY instead of offering a click that 400s. */}
											<TileDeleteButton
												label={copy.deleteAria(position)}
												title={theme.deletableImages.has(image.assetId)
													? copy.delete
													: copy.deleteBlocked}
												disabled={theme.busy || !theme.deletableImages.has(image.assetId)}
												onClick={() => void theme.deleteImage(image.assetId)}
											/>
										</TileControls>
									)}
								</ImageTile>
							);
						})}

						<AddImageLabel $disabled={theme.busy || theme.uploading || atCap}>
							<HiddenFileInput
								type="file"
								// One dialog fills the library (ui#664). The hook admits as many as there are
								// free slots and says what it did with the rest.
								multiple
								// The api derives the stored type from magic bytes, so this is a courtesy
								// filter in the picker, never the check that matters.
								accept={THEME_IMAGE_ACCEPT_ATTRIBUTE}
								aria-label={copy.addAria}
								disabled={theme.busy || theme.uploading || atCap}
								onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
									const picked = Array.from(event.target.files ?? []);
									// Reset first: picking the SAME file twice fires no change event
									// otherwise, so a rejected upload could not be retried.
									event.target.value = "";
									if (picked.length > 0) {
										void theme.uploadImages(picked);
									}
								}}
							/>
							{theme.uploading ? copy.uploading : copy.add}
						</AddImageLabel>
					</ImageRow>
					<Counter>{copy.counter(theme.images.length, MAX_THEME_IMAGES)}</Counter>
					{theme.uploadReport !== null && (
						/* A live region, so the outcome of a pick reaches a sender who is not looking at the
						   row — the file dialog took their attention away from it. Keyed by report so a
						   repeated identical outcome REMOUNTS and is announced again; re-rendering the same
						   characters mutates no DOM, and an alert that does not change stays silent. */
						<PickedImageOutcome key={theme.uploadReport.reportId} title={copy.problems}>
							{theme.uploadReport.busy > 0 && (
								<li>{copy.stillUploading(theme.uploadReport.busy)}</li>
							)}
							{theme.uploadReport.refused > 0 && (
								<li>{copy.overflow(theme.uploadReport.refused, MAX_THEME_IMAGES)}</li>
							)}
							{theme.uploadReport.failures.map((failure, at) => {
								const reason = failure.message.catalog === "theme"
									? copy.errors[failure.message.messageKey]
									: m.errors[failure.message.messageKey];
								return <li key={`${at}-${failure.name}`}>{copy.failedItem(failure.name, reason)}</li>;
							})}
						</PickedImageOutcome>
					)}

					<ToggleRow>
						<ToggleCopy>
							<ToggleName>{copy.enableLabel}</ToggleName>
							<Hint>{copy.enableHint}</Hint>
						</ToggleCopy>
						<Switch
							type="button"
							role="switch"
							$on={theme.enabled}
							aria-checked={theme.enabled}
							aria-label={copy.enableLabel}
							disabled={theme.busy}
							onClick={() => theme.setEnabled(!theme.enabled)}
						>
							<span/>
						</Switch>
					</ToggleRow>

					{dropped > 0 && <Hint>{copy.publishDrops(dropped)}</Hint>}
					<Actions>
						<GhostButton
							disabled={theme.busy || !theme.canRemove}
							onClick={() => void theme.remove()}
						>
							{copy.removeTheme}
						</GhostButton>
						<SaveButton
							disabled={theme.busy || !theme.canSave}
							onClick={() => void theme.save()}
						>
							{copy.publish}
						</SaveButton>
					</Actions>
				</>
			)}
		</Section>
	);
}
